import { Dialog } from '@/shared/ui/Dialog'
import LokiLogo from '@/shared/assets/icons/loki_logo.svg'
import {
  ArrowRight,
  ExternalLink,
  FileText,
  Layers,
  ShieldCheck,
  Sparkle,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { Box, Stack, styled } from 'styled-system/jsx'
import { css } from 'styled-system/css'
import { Heading, Text } from 'ui-kit/components'

const SILOW_URL = 'https://silow.ai'
const STORY_URL =
  'https://www.linkedin.com/feed/update/urn:li:activity:7480984374671351808/'

type Feature = {
  icon: ComponentType<{ size?: number | string }>
  title: string
  description: string
}

const FEATURES: Feature[] = [
  {
    icon: Layers,
    title: 'A new direction',
    description:
      'silow.ai is a work-data layer that turns real work into a prioritized AI roadmap and measurable ROI.',
  },
  {
    icon: ShieldCheck,
    title: 'Your data stays yours',
    description:
      'We built silow.ai with privacy and security by design. You own your data — always.',
  },
  {
    icon: Sparkle,
    title: 'Built for greater impact',
    description:
      "We're doubling down on what matters most: helping teams ship AI that delivers real results.",
  },
]

export const ShutdownModal = () => {
  return (
    <Dialog
      isOpen
      // No-op: the modal is fully non-closable (no X, no backdrop/Escape dismiss).
      onClose={() => {}}
      closable={false}
      showCloseButton={false}
      size="md"
      width="min(600px, calc(100% - 24px))"
      positionerClassname={css({
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(6px)',
      })}
    >
      <Stack gap="6" p={{ base: '6', md: '8' }}>
        {/* Pill */}
        <Box display="flex" justifyContent="center">
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            px="3"
            py="1.5"
            rounded="full"
            bg="colorPalette.a3"
            borderWidth="1px"
            borderColor="colorPalette.a6"
          >
            <Box w="6px" h="6px" rounded="full" bg="colorPalette.default" />
            <Text
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="wider"
              textTransform="uppercase"
              color="colorPalette.default"
            >
              Important update
            </Text>
          </Box>
        </Box>

        {/* Headline + subtext */}
        <Stack gap="3" textAlign="center">
          <Heading size="3xl" lineHeight="1.2">
            Loki.build is pausing.
            <br />
            We&apos;re focusing on{' '}
            <styled.a
              href={SILOW_URL}
              color="colorPalette.default"
              textDecoration="none"
              _hover={{
                textDecoration: 'underline',
                color: 'colorPalette.emphasized',
              }}
            >
              silow.ai
            </styled.a>
            .
          </Heading>
          <Text color="fg.muted" fontSize="md" maxW="440px" mx="auto">
            We&apos;re pausing development on Loki.build to put our full focus
            on building{' '}
            <styled.a
              href={SILOW_URL}
              color="colorPalette.default"
              fontWeight="medium"
              textDecoration="none"
              _hover={{
                textDecoration: 'underline',
                color: 'colorPalette.emphasized',
              }}
            >
              silow.ai
            </styled.a>{' '}
            — our new product.
          </Text>
        </Stack>

        {/* Divider */}
        <Box borderTopWidth="1px" borderColor="border.default" />

        {/* Feature list */}
        <Stack gap="5">
          {FEATURES.map(({ icon: Icon, title, description }) => (
            <Box key={title} display="flex" gap="4" alignItems="flex-start">
              <Box
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="44px"
                h="44px"
                rounded="xl"
                bg="colorPalette.a3"
                borderWidth="1px"
                borderColor="colorPalette.a6"
                color="colorPalette.default"
              >
                <Icon size={22} />
              </Box>
              <Stack gap="1" pt="0.5">
                <Text fontSize="md" fontWeight="semibold" color="fg.default">
                  {title}
                </Text>
                <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
                  {description}
                </Text>
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Story card */}
        <styled.a
          href={STORY_URL}
          target="_blank"
          rel="noopener noreferrer"
          display="flex"
          alignItems="center"
          gap="3"
          p="4"
          rounded="xl"
          borderWidth="1px"
          borderColor="border.default"
          bg="rgba(255, 255, 255, 0.03)"
          textDecoration="none"
          transition="background 0.15s, border-color 0.15s"
          _hover={{
            bg: 'rgba(255, 255, 255, 0.06)',
            borderColor: 'colorPalette.a7',
          }}
          _focusVisible={{
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: 'colorPalette.default',
            outlineOffset: '2px',
          }}
        >
          <Box
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="40px"
            h="40px"
            rounded="lg"
            bg="rgba(255, 255, 255, 0.05)"
            color="fg.default"
          >
            <FileText size={20} />
          </Box>
          <Stack gap="0.5" flex="1">
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              Why we&apos;re making this change
            </Text>
            <Text fontSize="sm" color="fg.muted">
              Read the full story in our post.
            </Text>
          </Stack>
          <Box color="fg.muted" flexShrink={0}>
            <ExternalLink size={18} />
          </Box>
        </styled.a>

        {/* CTA */}
        <styled.a
          href={SILOW_URL}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="2"
          w="full"
          h="52px"
          rounded="xl"
          bg="colorPalette.default"
          color="#0A0A0A"
          fontSize="md"
          fontWeight="bold"
          textDecoration="none"
          transition="background 0.15s"
          _hover={{ bg: 'colorPalette.emphasized' }}
          _focusVisible={{
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: 'colorPalette.default',
            outlineOffset: '3px',
          }}
        >
          Continue to silow.ai
          <ArrowRight size={18} />
        </styled.a>

        {/* Footer */}
        <Box display="flex" justifyContent="center" pt="1" opacity={0.7}>
          <img src={LokiLogo} alt="loki.build" style={{ height: '20px' }} />
        </Box>
      </Stack>
    </Dialog>
  )
}
